// The add-ons row on a product page draws the builder's own card snippet, so it
// has to answer the handful of calls that markup makes. None of the builder's
// machinery comes with it: there is no ladder here, no gifts and no rail — just
// a set of extras that ride along with the product when it goes in the cart.
//
// Ticking one puts it in the page's upsell list, which is what the add to cart
// button on this page already sends, keyed by handle the way it always was. The
// list lives on the product form's own scope, so the markup hands it in.
//
// An extra the shop sells in more than one variant is configured first, in the
// same modal the builder opens, because the row draws the same card and a card
// that ticks straight in would pick the variant for the buyer.

// A page can carry more than one row of extras — the ones made for the product
// and the accessories under them — so each keeps its own entry in the shared
// figure below rather than overwriting a single one.
let nextRowKey = 0;

const initProductAddons = ($el) => {
  const rowKey = `addons-${nextRowKey}`;
  nextRowKey += 1;

  const isVariantPicker = $el.dataset.source === "product_variants";

  const rawPayload = utils.JSONParse($el.querySelector("[data-product-addons-data]")?.innerHTML);

  const buildVariantItem = (variant) => ({
    ...variant,
    lowest_price: variant.price,
    variants: { kind: null, label: "", items: [] },
    attachments: [],
  });

  const payload = isVariantPicker
    ? (rawPayload?.items ?? []).map(buildVariantItem)
    : rawPayload ?? [];

  const productsById = new Map();
  const rememberProduct = (item) => {
    if (item && !productsById.has(item.id)) productsById.set(item.id, item);
  };
  payload.forEach((item) => {
    rememberProduct(item);
    (item.attachments ?? []).forEach(rememberProduct);
  });

  const getProduct = (id) => productsById.get(id);

  const listVariants = (item) => item?.variants?.items ?? [];

  // No gift ladder on this row, so nothing is ever free. The card reads it all
  // the same.
  const isFree = () => false;

  const money = (cents) => utils.formatMoney(cents).replace(/([.,])00(?!\d)/, "");

  const preselectedIds = isVariantPicker ? [] : utils.JSONParse($el.dataset.preselected) ?? [];

  const state = window.Alpine.reactive({
    picked_ids: preselectedIds,
    picked_variant_id: {},

    modal: {
      open: false,
      product_id: null,
      draft_variant_id: null,
      draft_attachments: [],
      was_selected: false,
    },
  });

  const isSelected = (id) => state.picked_ids.includes(id);

  // One variant is nothing to choose between, so it ticks straight in and the
  // modal stays shut.
  const hasVariants = (id) => listVariants(getProduct(id)).length > 1;

  const getPickedVariant = (id) =>
    listVariants(getProduct(id)).find((variant) => variant.id === state.picked_variant_id[id]) ??
    null;

  const priceOf = (id) => {
    const product = getProduct(id);
    if (!product) return 0;
    if (hasVariants(id)) {
      const picked = getPickedVariant(id);
      return picked ? picked.price : product.lowest_price;
    }
    return product.price;
  };

  const compareOf = (id) => {
    const product = getProduct(id);
    if (!product) return 0;
    const priceSource = hasVariants(id) ? getPickedVariant(id) : product;
    const compareAtPrice = priceSource?.compare_at_price ?? 0;
    return compareAtPrice > priceOf(id) ? compareAtPrice : 0;
  };

  const select = (id) => {
    if (!isSelected(id)) state.picked_ids = [...state.picked_ids, id];
  };

  const unselect = (id) => {
    state.picked_ids = state.picked_ids.filter((picked) => picked !== id);
    delete state.picked_variant_id[id];
  };

  const openVariants = (id) => {
    const product = getProduct(id);
    const variants = listVariants(product);
    state.modal.product_id = id;
    state.modal.was_selected = isSelected(id);
    state.modal.draft_variant_id =
      state.picked_variant_id[id] ??
      variants.find((variant) => variant.available)?.id ??
      variants[0]?.id ??
      null;
    state.modal.draft_attachments = (product?.attachments ?? [])
      .filter((attachment) => isSelected(attachment.id))
      .map((attachment) => attachment.id);
    state.modal.open = true;
  };

  const closeVariants = () => {
    state.modal.open = false;
    state.modal.product_id = null;
  };

  const setDraftVariant = (variantId) => {
    state.modal.draft_variant_id = variantId;
  };

  const getDraftVariant = () =>
    listVariants(getProduct(state.modal.product_id)).find(
      (variant) => variant.id === state.modal.draft_variant_id
    ) ?? null;

  const getDraftVariantList = () => listVariants(getProduct(state.modal.product_id));

  const toggleDraftAttachment = (attachmentId) => {
    const current = state.modal.draft_attachments;
    state.modal.draft_attachments = current.includes(attachmentId)
      ? current.filter((id) => id !== attachmentId)
      : [...current, attachmentId];
  };

  const sumDraftAttachments = () =>
    state.modal.draft_attachments.reduce(
      (total, attachmentId) => total + (getProduct(attachmentId)?.price ?? 0),
      0
    );

  const getDraftTotal = () => {
    const product = getProduct(state.modal.product_id);
    if (!product) return 0;
    const picked = getDraftVariant();
    return (picked ? picked.price : product.price) + sumDraftAttachments();
  };

  const getDraftCompareTotal = () => {
    const product = getProduct(state.modal.product_id);
    if (!product) return 0;
    const picked = getDraftVariant() ?? product;
    return (picked.compare_at_price || picked.price) + sumDraftAttachments();
  };

  const confirmVariants = () => {
    const id = state.modal.product_id;
    const product = getProduct(id);
    if (hasVariants(id) && !state.modal.draft_variant_id) return;

    state.picked_variant_id[id] = state.modal.draft_variant_id;
    select(id);

    (product?.attachments ?? []).forEach((attachment) => {
      if (state.modal.draft_attachments.includes(attachment.id)) select(attachment.id);
      else unselect(attachment.id);
    });

    closeVariants();
  };

  const removeFromVariants = () => {
    unselect(state.modal.product_id);
    closeVariants();
  };

  let onVariantChosen = null;

  const bindVariantChoice = (callback) => {
    onVariantChosen = callback;
  };

  const syncChosenVariant = (variantId) => {
    state.picked_ids = variantId ? [variantId] : [];
  };

  const chooseVariant = (variantId) => {
    const variant = getProduct(variantId);
    if (!variant?.available) return;
    state.picked_ids = [variantId];
    onVariantChosen?.(variantId);
  };

  const toggleProduct = (id) => {
    if (!getProduct(id)) return;
    if (isVariantPicker) {
      chooseVariant(id);
      return;
    }
    if (isSelected(id)) {
      unselect(id);
      return;
    }
    if (hasVariants(id)) {
      openVariants(id);
      return;
    }
    select(id);
  };

  // An add-on hanging off an add-on is still just something to tick.
  const toggleAttachment = (id) => {
    if (isSelected(id)) unselect(id);
    else select(id);
  };

  // What the buy button sends. Reading picked_ids here is what makes the markup's
  // x-effect run again on every tick, and writing the whole row each time means a
  // page that comes back from the cart cannot leave a stale entry behind.
  // What this row adds to the order, kept where the financing line can read it.
  // It is published rather than read off the upsell list because that list is a
  // Map, and a Map is not something every build of the framework watches; a
  // plain number on a plain store is watched by all of them.
  const publishAddonTotal = () => {
    window._stores = window._stores || {};
    if (!window._stores.apexAddonTotals) {
      window._stores.apexAddonTotals = window.Alpine.reactive({ rows: {} });
    }
    window._stores.apexAddonTotals.rows[rowKey] = state.picked_ids.reduce(
      (total, id) => total + priceOf(id),
      0
    );
  };

  const syncUpsellItems = (upsellItems) => {
    const picked = state.picked_ids;
    publishAddonTotal();
    if (!upsellItems) return;
    productsById.forEach((item) => {
      if (!item.handle) return;
      if (picked.includes(item.id)) {
        upsellItems.set(item.handle, {
          id: getPickedVariant(item.id)?.id ?? item.variant_id,
          product_id: item.id,
          properties: {},
          price: priceOf(item.id),
        });
      } else {
        upsellItems.delete(item.handle);
      }
    });
  };

  // The clip plays in the theme's own gallery, the same one the builder's cards
  // open, so a YouTube link and an uploaded file take the same path.
  const openVideo = (id) => {
    const clip = getProduct(id)?.video;
    const gallery = window._stores?.videoGallery;
    if (!clip || !gallery) return;

    const onPhone = window.matchMedia("(max-width: 767px)").matches;
    const media = onPhone ? clip.mobile : clip.desktop;
    const isLandscape = !onPhone || clip.mobile_is_landscape;

    gallery.openGallery({
      media: [media],
      index: 0,
      autoplay: "true",
      loop: "true",
      controls: "true",
      muted: "false",
      use_desktop_ratio: isLandscape,
    });
  };

  // A widened card cannot keep the square ratio or it would tower over its row,
  // so the square a normal card resolves to is measured and handed to the CSS.
  let lastMeasuredSide = null;

  const syncCardVisualHeight = () => {
    const grid = $el.querySelector(".grid");
    const normalCard = grid?.querySelector(".card:not(.span-half):not(.span-full) > .card-visual");
    if (!normalCard) return;
    const side = Math.round(normalCard.getBoundingClientRect().width);
    if (side <= 0 || side === lastMeasuredSide) return;
    lastMeasuredSide = side;
    grid.style.setProperty("--card-visual-h", `${side}px`);
  };

  const grid = $el.querySelector(".grid");
  if (grid && window.ResizeObserver) {
    new window.ResizeObserver(syncCardVisualHeight).observe(grid);
  } else {
    window.addEventListener("resize", syncCardVisualHeight);
    requestAnimationFrame(syncCardVisualHeight);
  }

  return {
    addons: state,
    modal: state.modal,
    money,
    getProduct,
    getPickedVariant,
    getDraftVariant,
    getDraftVariantList,
    getDraftTotal,
    getDraftCompareTotal,
    hasVariants,
    isFree,
    isSelected,
    priceOf,
    compareOf,
    toggleProduct,
    toggleAttachment,
    openVariants,
    closeVariants,
    setDraftVariant,
    toggleDraftAttachment,
    confirmVariants,
    removeFromVariants,
    syncUpsellItems,
    openVideo,
    bindVariantChoice,
    syncChosenVariant,
  };
};

window._sections = window._sections || {};
window._sections["initProductAddons"] = initProductAddons;
